import { useUpdateEffect } from 'react-use';

import { Translate, useFormController } from '@dreryk/react-common';
import {
   Form,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   StyledMargin,
   Toggle,
   Tooltip,
} from '@dreryk/react-components';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { useCurrentHealthcareProviderWaitlistTimeSlotReservedTokenInConfirmationEnabledSetCommand } from './PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggle.hooks';
import { PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggleLoader } from './PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggle.loader';
import { PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggleFormBean } from './PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggle.types';
import { StyledTooltipText } from './PatientPhoneNotificationTooltipText.styled';

type PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggleProps = {
   readonly responseTokenEnabled: boolean;
   readonly notificationEnabled: boolean;
};

export const PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggle = (
   props: PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggleProps
): JSX.Element => {
   const {
      register,
      handleSubmit,
      reset,
   } = useFormController<PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggleFormBean>({
      responseTokenEnabled: props.responseTokenEnabled,
   });

   useUpdateEffect(() => {
      reset({
         responseTokenEnabled: props.responseTokenEnabled,
      });
   }, [props.responseTokenEnabled]);

   const {
      executeAsync: onSubmit,
      loading,
   } = useCurrentHealthcareProviderWaitlistTimeSlotReservedTokenInConfirmationEnabledSetCommand();

   return (
      <StyledMargin left={32}>
         <StyledFlex gap={'12px'}>
            <Form onInput={handleSubmit(onSubmit)}>
               <Toggle
                  controller={register('responseTokenEnabled')}
                  testId={'patientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggle'}
                  disabled={loading || !props.notificationEnabled}
               />
            </Form>
            <StyledFlex gap={'4px'} alignItems={'center'}>
               <Translate>
                  {
                     patientPhoneNotificationTranslationKeys.notificationToggle
                        .waitlistProposedAppointmentResponseTokenToggle
                  }
               </Translate>
               <Tooltip
                  maxWidth={'500px'}
                  testId={'waitlistProposedAppointmentResponseTokenToggleTooltip'}
                  content={
                     <StyledTooltipText>
                        <Translate>
                           {
                              patientPhoneNotificationTranslationKeys.notificationToggle
                                 .waitlistProposedAppointmentResponseTokenToggleTooltip
                           }
                        </Translate>
                     </StyledTooltipText>
                  }
               >
                  <IconWrapper
                     testId={'waitlistProposedAppointmentResponseTokenToggleTooltipIconWrapper'}
                     fillColor={IconFillColor.PRIMARY}
                     size={IconSize.SMALL}
                  >
                     <QuestionmarkCircleIcon />
                  </IconWrapper>
               </Tooltip>
            </StyledFlex>
         </StyledFlex>
      </StyledMargin>
   );
};

export const PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggleWithLoader = (): JSX.Element => {
   return (
      <PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggleLoader>
         {({ responseTokenEnabled, notificationEnabled }) => (
            <PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggle
               responseTokenEnabled={responseTokenEnabled}
               notificationEnabled={notificationEnabled}
            />
         )}
      </PatientPhoneNotificationWaitlistProposedAppointmentResponseTokenToggleLoader>
   );
};
